import { useState } from "react";

export interface TimelineEvent {
  id: number;
  type: "status" | "field" | "billing" | "note" | "created" | "assigned" | "deliverable";
  title: string;
  description: string;
  user: string;
  avatar: string;
  timestamp: string;
  meta?: Record<string, string>;
}

interface ActivityTimelineProps {
  jobNo: string;
  jobTitle: string;
  assignedDate: string;
  status: string;
  fieldTeam: string[];
}

const TYPE_CONFIG: Record<TimelineEvent["type"], { icon: string; color: string; bg: string; label: string }> = {
  created:     { icon: "ri-file-add-line",        color: "#10b981", bg: "#ecfdf5",  label: "Created"     },
  assigned:    { icon: "ri-user-add-line",         color: "#3b82f6", bg: "#eff6ff",  label: "Assigned"    },
  status:      { icon: "ri-refresh-line",          color: "#f59e0b", bg: "#fffbeb",  label: "Status"      },
  field:       { icon: "ri-map-pin-user-line",     color: "#8b5cf6", bg: "#f5f3ff",  label: "Field"       },
  billing:     { icon: "ri-bill-line",             color: "#ef4444", bg: "#fef2f2",  label: "Billing"     },
  note:        { icon: "ri-chat-3-line",           color: "#64748b", bg: "#f8fafc",  label: "Note"        },
  deliverable: { icon: "ri-folder-check-line",     color: "#10b981", bg: "#ecfdf5",  label: "Deliverable" },
};

const FILTER_TYPES: Array<TimelineEvent["type"] | "all"> = ["all", "status", "field", "billing", "deliverable", "note"];

function buildTimeline(props: ActivityTimelineProps): TimelineEvent[] {
  const { jobNo, assignedDate, status, fieldTeam } = props;
  const base = new Date(assignedDate).getTime();
  const day = 86400000;

  const events: TimelineEvent[] = [
    {
      id: 1,
      type: "created",
      title: "Job Card Created",
      description: `Job card ${jobNo} was created and added to the system.`,
      user: "Priya Sharma",
      avatar: "PS",
      timestamp: new Date(base).toISOString(),
      meta: { "Job No": jobNo },
    },
    {
      id: 2,
      type: "assigned",
      title: "Field Team Assigned",
      description: `Team assigned: ${fieldTeam.join(", ")}`,
      user: "Priya Sharma",
      avatar: "PS",
      timestamp: new Date(base + 2 * 60 * 60 * 1000).toISOString(),
      meta: { "Team": fieldTeam.join(", ") },
    },
    {
      id: 3,
      type: "status",
      title: "Status → In Progress",
      description: "Job moved to In Progress. Field work commenced.",
      user: "System",
      avatar: "SY",
      timestamp: new Date(base + day).toISOString(),
      meta: { "From": "Pending", "To": "In Progress" },
    },
    {
      id: 4,
      type: "field",
      title: "Field Check-In",
      description: `${fieldTeam[0] || "Ravi Teja"} checked in at site. Total station set up at BM-01.`,
      user: fieldTeam[0] || "Ravi Teja",
      avatar: (fieldTeam[0] || "Ravi Teja").split(" ").map((n) => n[0]).join("").slice(0, 2),
      timestamp: new Date(base + day + 8 * 60 * 60 * 1000).toISOString(),
      meta: { "Location": "Site BM-01", "Check-in": "08:15 AM", "Duration": "6h 45m" },
    },
    {
      id: 5,
      type: "field",
      title: "Field Check-Out",
      description: `${fieldTeam[0] || "Ravi Teja"} completed field session. 24 control points established.`,
      user: fieldTeam[0] || "Ravi Teja",
      avatar: (fieldTeam[0] || "Ravi Teja").split(" ").map((n) => n[0]).join("").slice(0, 2),
      timestamp: new Date(base + day + 15 * 60 * 60 * 1000).toISOString(),
      meta: { "Check-out": "03:00 PM", "Photos": "7 uploaded", "Notes": "Rocky terrain on north side" },
    },
    {
      id: 6,
      type: "deliverable",
      title: "Deliverable Uploaded",
      description: "Survey_Report_Draft_v1.pdf uploaded for review.",
      user: "Amit Patel",
      avatar: "AP",
      timestamp: new Date(base + 3 * day).toISOString(),
      meta: { "File": "Survey_Report_Draft_v1.pdf", "Status": "Draft", "Size": "2.4 MB" },
    },
    {
      id: 7,
      type: "note",
      title: "Note Added",
      description: "Rocky terrain on the north side. Field team should carry extra equipment. Coordinate with site manager Mr. Reddy before entry.",
      user: "Priya Sharma",
      avatar: "PS",
      timestamp: new Date(base + 3 * day + 2 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 8,
      type: "billing",
      title: "Quotation Generated",
      description: `Quotation QT-2026-0034 raised for ${jobNo}. Amount: ₹1,25,000`,
      user: "Priya Sharma",
      avatar: "PS",
      timestamp: new Date(base + 4 * day).toISOString(),
      meta: { "Quotation No": "QT-2026-0034", "Amount": "₹1,25,000", "Status": "Sent to Client" },
    },
    {
      id: 9,
      type: "field",
      title: "Second Field Session",
      description: `${fieldTeam[1] || fieldTeam[0] || "Suresh Kumar"} conducted follow-up survey on south boundary.`,
      user: fieldTeam[1] || fieldTeam[0] || "Suresh Kumar",
      avatar: (fieldTeam[1] || fieldTeam[0] || "Suresh Kumar").split(" ").map((n) => n[0]).join("").slice(0, 2),
      timestamp: new Date(base + 5 * day + 8 * 60 * 60 * 1000).toISOString(),
      meta: { "Location": "South Boundary", "Duration": "5h 30m", "Photos": "4 uploaded" },
    },
    {
      id: 10,
      type: "deliverable",
      title: "CAD Drawing Uploaded",
      description: "Topographic_Map_v2.dwg uploaded. Under review by office team.",
      user: "Amit Patel",
      avatar: "AP",
      timestamp: new Date(base + 6 * day).toISOString(),
      meta: { "File": "Topographic_Map_v2.dwg", "Status": "Under Review", "Size": "8.7 MB" },
    },
    ...(status === "Completed" || status === "Delivered" ? [
      {
        id: 11,
        type: "status" as TimelineEvent["type"],
        title: "Status → Completed",
        description: "All field work and office processing completed. Ready for delivery.",
        user: "Priya Sharma",
        avatar: "PS",
        timestamp: new Date(base + 8 * day).toISOString(),
        meta: { "From": "In Progress", "To": "Completed" },
      },
      {
        id: 12,
        type: "billing" as TimelineEvent["type"],
        title: "Invoice Generated",
        description: `Invoice INV-2026-0034 raised. Amount: ₹1,25,000 (incl. GST)`,
        user: "Priya Sharma",
        avatar: "PS",
        timestamp: new Date(base + 8 * day + 3 * 60 * 60 * 1000).toISOString(),
        meta: { "Invoice No": "INV-2026-0034", "Amount": "₹1,25,000", "GST": "₹22,500", "Status": "Sent" },
      },
    ] : []),
    ...(status === "Delivered" ? [
      {
        id: 13,
        type: "deliverable" as TimelineEvent["type"],
        title: "Final Package Delivered",
        description: "All deliverables packaged and sent to client. Client acknowledged receipt.",
        user: "Priya Sharma",
        avatar: "PS",
        timestamp: new Date(base + 10 * day).toISOString(),
        meta: { "Package": "Final_Delivery_Package.zip", "Acknowledged": "Yes" },
      },
      {
        id: 14,
        type: "status" as TimelineEvent["type"],
        title: "Status → Delivered",
        description: "Job card marked as Delivered. All deliverables acknowledged by client.",
        user: "System",
        avatar: "SY",
        timestamp: new Date(base + 10 * day + 1 * 60 * 60 * 1000).toISOString(),
        meta: { "From": "Completed", "To": "Delivered" },
      },
    ] : []),
  ];

  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function formatTimestamp(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function ActivityTimeline({ jobNo, jobTitle, assignedDate, status, fieldTeam }: ActivityTimelineProps) {
  const [filter, setFilter] = useState<TimelineEvent["type"] | "all">("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const allEvents = buildTimeline({ jobNo, jobTitle, assignedDate, status, fieldTeam });
  const filtered = filter === "all" ? allEvents : allEvents.filter((e) => e.type === filter);

  const counts: Record<string, number> = { all: allEvents.length };
  allEvents.forEach((e) => { counts[e.type] = (counts[e.type] || 0) + 1; });

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_TYPES.map((t) => {
            const cfg = t === "all" ? null : TYPE_CONFIG[t];
            const count = counts[t] || 0;
            const isActive = filter === t;
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {cfg && <span className="w-3 h-3 flex items-center justify-center"><i className={`${cfg.icon} text-xs`} /></span>}
                <span className="capitalize">{t === "all" ? "All Events" : cfg?.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${isActive ? "bg-white/20 text-white" : "bg-white text-gray-600"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-sm text-gray-800">
            {filtered.length} event{filtered.length !== 1 ? "s" : ""}
            {filter !== "all" && <span className="text-gray-400 font-normal"> · {TYPE_CONFIG[filter]?.label}</span>}
          </h3>
          <span className="text-xs text-gray-400">Most recent first</span>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-10">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
              <i className="ri-time-line text-xl text-gray-400" />
            </div>
            <p className="text-sm text-gray-400">No events for this filter</p>
          </div>
        )}

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-100" />

          <div className="space-y-1">
            {filtered.map((event, idx) => {
              const cfg = TYPE_CONFIG[event.type];
              const isExpanded = expandedId === event.id;
              const isLast = idx === filtered.length - 1;

              return (
                <div key={event.id} className={`relative flex gap-4 ${!isLast ? "pb-1" : ""}`}>
                  {/* Icon */}
                  <div className="flex-shrink-0 z-10">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-white"
                      style={{ background: cfg.bg }}
                    >
                      <i className={`${cfg.icon} text-sm`} style={{ color: cfg.color }} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-5">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : event.id)}
                      className="w-full text-left"
                    >
                      <div className={`rounded-xl border p-3.5 transition-all cursor-pointer ${isExpanded ? "border-gray-200 bg-gray-50" : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className="text-xs px-2 py-0.5 rounded-full font-semibold"
                                style={{ color: cfg.color, background: cfg.bg }}
                              >
                                {cfg.label}
                              </span>
                              <p className="text-sm font-semibold text-gray-900">{event.title}</p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{event.description}</p>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(event.timestamp)}</p>
                            <i className={isExpanded ? "ri-arrow-up-s-line text-gray-300 text-sm mt-0.5" : "ri-arrow-down-s-line text-gray-300 text-sm mt-0.5"} />
                          </div>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {event.avatar}
                              </div>
                              <span className="text-xs text-gray-600 font-medium">{event.user}</span>
                              <span className="text-gray-300">·</span>
                              <span className="text-xs text-gray-400">{formatTimestamp(event.timestamp)}</span>
                            </div>
                            {event.meta && Object.keys(event.meta).length > 0 && (
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                {Object.entries(event.meta).map(([k, v]) => (
                                  <div key={k} className="bg-white rounded-lg p-2 border border-gray-100">
                                    <p className="text-xs text-gray-400">{k}</p>
                                    <p className="text-xs font-semibold text-gray-800 mt-0.5">{v}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
